/**
 * Cross-trajectory projection helpers — lazy compute for the Compare flow.
 *
 * On Compare-button press:
 *   1. The primary's playhead is read; its (lat, lon) at that UTC is
 *      projected onto the raceline → `primaryAtSMeters`.
 *   2. The user picks another run from the modal.
 *   3. `computeCompareSeed(run, primaryAtSMeters, raceline)` fetches the
 *      picked run's full GPS, projects every sample, and finds the
 *      sample whose `s_m` is closest to `primaryAtSMeters` (with `d_m`
 *      below `MAX_PROJECTION_M` so cars on completely different lines
 *      don't false-match).
 *   4. The returned `seedUtcMs` + `sessionId` seeds the secondary
 *      HLSPlayer to the equivalent moment.
 *
 * After the seed, both players run independently — there's no further
 * cross-trajectory work to do, so this lives as a one-shot util rather
 * than a long-lived hook.
 */

import api from '../services/api';
import { nearestPointOnPolyline } from '../plugins/synced_viewer/util';
import type { Run } from '../services/fleet';
import type { RacelineTrimContext } from '../hooks/useDeviceTrail';
import type { SessionTelemetrySample } from '../types';

const MAX_PROJECTION_M = 50;
const SEED_TELEMETRY_LIMIT = 50_000;

export interface CompareSeed {
  runId: string;
  sessionId: string;
  /** The UTC at which the picked run was at the primary's s. */
  seedUtcMs: number;
  /** B's lateral offset at that moment (meters). */
  d_m: number;
  /** The picked run's s at that sample (≈ primaryAtSMeters within tolerance). */
  s_m: number;
}

interface ProjectedSample {
  t_ms: number;
  s_m: number;
  d_m: number;
}

function projectTrack(
  samples: SessionTelemetrySample[],
  ctx: RacelineTrimContext,
): ProjectedSample[] {
  const out: ProjectedSample[] = [];
  for (const s of samples) {
    const g = s.gps;
    if (!g || g.lat == null || g.lon == null) continue;
    const t = g.fix_ts_ms ?? s.ts_ms;
    if (typeof t !== 'number' || !Number.isFinite(t)) continue;
    const proj = nearestPointOnPolyline(ctx.coords, ctx.cumulativeS, g.lat, g.lon);
    if (!proj) continue;
    if (proj.d_m > (ctx.maxOffsetM ?? MAX_PROJECTION_M)) continue;
    if (proj.s_m < 0 || proj.s_m > ctx.totalLengthM) continue;
    out.push({ t_ms: t, s_m: proj.s_m, d_m: proj.d_m });
  }
  return out;
}

/** For a run, find the sample whose projected s on the raceline is
 * closest to `targetS`. Returns null if the run never came within the
 * raceline buffer or never reached `targetS`. */
export async function computeCompareSeed(
  run: Run,
  targetS: number,
  racelineCtx: RacelineTrimContext,
): Promise<CompareSeed | null> {
  if (!run.sessions.length) return null;
  const batches = await Promise.all(
    run.sessions.map((s) =>
      api
        .listSessionTelemetry(s.session_id, { kind: 'gps', limit: SEED_TELEMETRY_LIMIT })
        .catch(() => [] as SessionTelemetrySample[]),
    ),
  );
  const merged: SessionTelemetrySample[] = [];
  for (const b of batches) {
    for (const s of b) merged.push(s);
  }
  merged.sort((a, b) => {
    const at = a.gps?.fix_ts_ms ?? a.ts_ms ?? 0;
    const bt = b.gps?.fix_ts_ms ?? b.ts_ms ?? 0;
    return at - bt;
  });
  const projected = projectTrack(merged, racelineCtx);
  if (projected.length === 0) return null;
  let bestIdx = 0;
  let bestDelta = Math.abs(projected[0].s_m - targetS);
  for (let i = 1; i < projected.length; i += 1) {
    const d = Math.abs(projected[i].s_m - targetS);
    if (d < bestDelta) {
      bestDelta = d;
      bestIdx = i;
    }
  }
  const best = projected[bestIdx];
  const sessionId = resolveSessionForUtc(run, best.t_ms) ?? run.sessions[run.sessions.length - 1].session_id;
  return {
    runId: run.run_id,
    sessionId,
    seedUtcMs: best.t_ms,
    d_m: best.d_m,
    s_m: best.s_m,
  };
}

/** Pick the run's constituent session whose [start_utc_ms, end_utc_ms]
 * window contains `utcMs`. Falls back to the closest session by start
 * time when no window contains it (legacy sessions where end_utc_ms
 * may be null get treated as open-ended on the right). */
function resolveSessionForUtc(run: Run, utcMs: number): string | null {
  for (const s of run.sessions) {
    const start = s.start_utc_ms ?? -Infinity;
    const end = s.end_utc_ms ?? Infinity;
    if (utcMs >= start && utcMs <= end) return s.session_id;
  }
  let best: { sid: string; delta: number } | null = null;
  for (const s of run.sessions) {
    if (s.start_utc_ms == null) continue;
    const delta = Math.abs(s.start_utc_ms - utcMs);
    if (!best || delta < best.delta) best = { sid: s.session_id, delta };
  }
  return best?.sid ?? null;
}
